import React, { createContext, useContext, useState, useCallback } from 'react'
import { Forecast, Location } from '../types'
import { forecastApi } from '../api/client'

interface WeatherContextType {
  forecast: Forecast | null
  location: Location | null
  isLoading: boolean
  error: string | null
  fetchForecast: (location: Location) => Promise<void>
  setLocation: (location: Location) => void
}

const WeatherContext = createContext<WeatherContextType | undefined>(undefined)

export const WeatherProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [forecast, setForecast] = useState<Forecast | null>(null)
  const [location, setLocationState] = useState<Location | null>({
    latitude: 40.7128,
    longitude: -74.0060,
    display_name: 'New York City, NY',
    timezone: 'America/New_York',
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchForecast = useCallback(async (loc: Location) => {
    setIsLoading(true)
    setError(null)

    try {
      const data = await forecastApi.getForecast(
        loc.latitude,
        loc.longitude,
        loc.display_name,
        loc.timezone
      )
      setForecast(data)
      setLocationState(loc)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch forecast'
      setError(errorMessage)
      console.error('Error fetching forecast:', err)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const setLocation = useCallback((loc: Location) => {
    setLocationState(loc)
    fetchForecast(loc)
  }, [fetchForecast])

  // Fetch initial forecast on mount
  React.useEffect(() => {
    if (location) {
      fetchForecast(location)
    }
  }, [])

  return (
    <WeatherContext.Provider
      value={{
        forecast,
        location,
        isLoading,
        error,
        fetchForecast,
        setLocation,
      }}
    >
      {children}
    </WeatherContext.Provider>
  )
}

export const useWeather = () => {
  const context = useContext(WeatherContext)
  if (context === undefined) {
    throw new Error('useWeather must be used within a WeatherProvider')
  }
  return context
}
